import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Users, CheckCircle2, Clock, TrendingUp, Zap, Briefcase, MessageSquare,
    FileText, ArrowRight, RefreshCw, Plus, Sparkles, Crown, Info, Store,
    Landmark, ChevronRight, Activity, Building2, Calendar,
} from "lucide-react";
import { motion } from "framer-motion";
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip,
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import api, { resolveAvatar } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import CommandBar from "@/components/CommandBar";
import UsageWidget from "@/components/UsageWidget";

/* ================================================================
   Nexus One Home Dashboard — Sprint I-3 (polish pass)
   Palantir × Linear × Attio — dark enterprise B2B.
   ================================================================ */

const KPI_ICONS = { Users, CheckCircle2, Clock, TrendingUp, Sparkles, Zap, Briefcase, MessageSquare, FileText };
const KPI_KEYS  = ["employees_active", "tasks_completed_today", "hours_saved_month", "labor_savings_month"];
const CHART_COLORS = ["#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ec4899", "#3b82f6", "#f97316"];

const PLAN_CHIPS = {
    starter:          { label: "Starter",    cls: "bg-white/5 text-[color:var(--atlas-text-secondary)] border-[color:var(--atlas-border-strong)]" },
    pro:              { label: "Pro",        cls: "bg-[color:var(--atlas-info-muted)] text-[#93c5fd] border-[color:rgba(59,130,246,0.45)]" },
    business:         { label: "Business",   cls: "bg-[color:var(--atlas-primary-muted)] text-[color:var(--atlas-primary-hover)] border-[color:rgba(139,92,246,0.45)]" },
    enterprise:       { label: "Enterprise", cls: "bg-[color:var(--atlas-warning-muted)] text-[#fbbf24] border-[color:rgba(245,158,11,0.45)]" },
    owner_unlimited:  { label: "OWNER",      cls: "bg-[color:var(--atlas-warning-muted)] text-[#fbbf24] border-[color:rgba(245,158,11,0.45)] font-bold" },
    lifetime_free:    { label: "LIFETIME",   cls: "bg-[color:var(--atlas-success-muted)] text-[#34d399] border-[color:rgba(16,185,129,0.45)] font-bold" },
};

/* mocked SAM.gov-style federal opportunities preview (live in Phase 5b) */
const GOV_OPPS = [
    { agency: "GSA",  title: "Multi-Agency AI Contract Vehicle",       value_label: "$45M+", deadline_days: 42, fit_score: 92, naics: "541511" },
    { agency: "USDA", title: "Rural Broadband Modernization",          value_label: "$32M+", deadline_days: 58, fit_score: 74, naics: "517311" },
    { agency: "VA",   title: "Veterans Digital Services Platform",     value_label: "$25M+", deadline_days: 34, fit_score: 81, naics: "541512" },
    { agency: "DHS",  title: "Cybersecurity Threat Intelligence Prog.", value_label: "$18M+", deadline_days: 27, fit_score: 88, naics: "541519" },
];

/* Framer stagger settings */
const staggerContainer = {
    hidden: {},
    show:   { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};
const rise = {
    hidden: { opacity: 0, y: 8 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

/* ================================================================ */

function fmtDateTime(iso, tz) {
    try {
        return new Intl.DateTimeFormat("en-US", {
            weekday: "long", month: "short", day: "numeric",
            hour: "numeric", minute: "2-digit", hour12: true,
            timeZone: tz || undefined,
        }).format(new Date(iso));
    } catch { return new Date(iso).toLocaleString(); }
}

function relTime(iso) {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)    return "just now";
    if (diff < 3600)  return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

function fmtNumber(n)  { return (n ?? 0).toLocaleString(); }
function fmtUsd(n)     { return `$${fmtNumber(n)}`; }

/* count-up hook: animates a numeric value from 0 to `target` over `duration` ms */
function useCountUp(target, duration = 800) {
    const [v, setV] = useState(0);
    const rafRef = useRef(null);
    const startRef = useRef(null);
    useEffect(() => {
        if (target == null || Number.isNaN(target)) return;
        const startVal = 0;
        const delta = target - startVal;
        cancelAnimationFrame(rafRef.current);
        startRef.current = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - startRef.current) / duration);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - t, 3);
            setV(startVal + delta * eased);
            if (t < 1) rafRef.current = requestAnimationFrame(tick);
            else setV(target);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, duration]);
    return v;
}

/* ================================================================
   AppHome
   ================================================================ */
export default function AppHome() {
    const { user } = useAuth();
    const nav = useNavigate();

    const [summary, setSummary]           = useState(null);
    const [team, setTeam]                 = useState(null);
    const [activity, setActivity]         = useState(null);
    const [briefing, setBriefing]         = useState(null);
    const [briefingBusy, setBriefingBusy] = useState(false);
    const [performers, setPerformers]     = useState(null);
    const [showFormula, setShowFormula]   = useState(false);
    const [cmdOpen, setCmdOpen]           = useState(false);

    const impersonating = user?.impersonating;

    const loadSummary    = () => api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
    const loadTeam       = () => api.get("/dashboard/team").then((r) => setTeam(r.data)).catch(() => setTeam({ team: [], active_count: 0, cap: 5, unlimited: false }));
    const loadActivity   = () => api.get("/dashboard/activity?scope=company&limit=12").then((r) => setActivity(r.data)).catch(() => setActivity({ activity: [] }));
    const loadBriefing   = () => api.get("/dashboard/briefing").then((r) => setBriefing(r.data)).catch(() => {});
    const loadPerformers = () => api.get("/dashboard/top-performers?limit=5").then((r) => setPerformers(r.data)).catch(() => setPerformers({ performers: [] }));

    useEffect(() => {
        loadSummary(); loadTeam(); loadActivity(); loadBriefing(); loadPerformers();
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdOpen((v) => !v);
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const refreshAll = () => { loadSummary(); loadTeam(); loadActivity(); loadPerformers(); };

    const refreshBriefing = async () => {
        if (impersonating) return;
        setBriefingBusy(true);
        try {
            const r = await api.post("/dashboard/briefing/refresh");
            setBriefing(r.data);
            toast.success("Briefing refreshed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refresh failed");
        }
        setBriefingBusy(false);
    };

    const hero = summary?.hero;
    const tiles = useMemo(() => {
        const map = new Map((summary?.tiles || []).map((t) => [t.key, t]));
        return KPI_KEYS.map((k) => map.get(k)).filter(Boolean);
    }, [summary]);

    const planChip  = PLAN_CHIPS[hero?.plan] || PLAN_CHIPS.starter;
    const isOwner   = hero?.is_owner;
    const isLifetime= hero?.is_lifetime;

    /* department distribution (donut) */
    const workforceDist = useMemo(() => {
        const t = team?.team || [];
        const counts = {};
        t.forEach((m) => {
            const dept = (m.role || "Other").split("·")[0].trim() || "Other";
            counts[dept] = (counts[dept] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    }, [team]);

    /* last-7d line chart derived from recent activity */
    const perf7d = useMemo(() => {
        const days = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - i);
            days.push({ key: d.toISOString().slice(0, 10), label: d.toLocaleDateString("en-US", { weekday: "short" }), tasks: 0 });
        }
        const idx = Object.fromEntries(days.map((d, i) => [d.key, i]));
        (activity?.activity || []).forEach((a) => {
            const k = (a.timestamp || "").slice(0, 10);
            if (k in idx && a.status === "done") days[idx[k]].tasks += 1;
        });
        return days;
    }, [activity]);

    return (
        <div className="h-full overflow-y-auto atlas-scrollbar" data-testid="dashboard-root">
            <div className="max-w-[1500px] mx-auto px-8 py-8 space-y-8">
                {/* HERO ROW ================================================= */}
                <HeroRow
                    hero={hero}
                    planChip={planChip}
                    isOwner={isOwner}
                    isLifetime={isLifetime}
                    onRefresh={refreshAll}
                    onNew={() => setCmdOpen(true)}
                />

                {/* KPI GRID ================================================= */}
                {!summary ? (
                    <SkeletonGrid rows={1} cols={4} height="h-40" />
                ) : (
                    <motion.section
                        variants={staggerContainer}
                        initial="hidden"
                        animate="show"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                        data-testid="kpi-grid"
                    >
                        {tiles.map((t, i) => (
                            <motion.div variants={rise} key={t.key}>
                                <KpiCard
                                    tile={t}
                                    starMetric={i === 3}
                                    onOpen={() => {
                                        if (t.modal === "labor_savings_formula") setShowFormula(true);
                                        else if (t.deep_link) nav(t.deep_link);
                                    }}
                                />
                            </motion.div>
                        ))}
                    </motion.section>
                )}

                {/* QUICK ACTIONS ============================================ */}
                <QuickActionsRow onNew={() => setCmdOpen(true)} />

                {/* 2-COLUMN SPLIT: donut + line chart ======================= */}
                <motion.section
                    variants={staggerContainer}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 lg:grid-cols-12 gap-4"
                >
                    <motion.div variants={rise} className="lg:col-span-5">
                        <WorkforceMixCard data={workforceDist} total={team?.active_count ?? 0} />
                    </motion.div>
                    <motion.div variants={rise} className="lg:col-span-7">
                        <PerformanceCard data={perf7d} tasksTotal={perf7d.reduce((a, d) => a + d.tasks, 0)} />
                    </motion.div>
                </motion.section>

                {/* DAILY BRIEFING + TOP PERFORMERS ========================== */}
                <motion.section
                    variants={staggerContainer}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 lg:grid-cols-3 gap-4"
                >
                    <motion.div variants={rise} className="lg:col-span-2">
                        <BriefingCard
                            briefing={briefing}
                            onRefresh={refreshBriefing}
                            busy={briefingBusy}
                            disabled={impersonating}
                            hasNothing={team?.team?.length === 0}
                        />
                    </motion.div>
                    <motion.div variants={rise}>
                        <TopPerformersCard performers={performers?.performers} />
                    </motion.div>
                </motion.section>

                {/* RECENT ACTIVITY ========================================== */}
                <RecentActivityCard activity={activity?.activity || []} />

                {/* USAGE THIS MONTH ========================================= */}
                <UsageWidget />

                {/* GOVERNMENT OPPORTUNITIES ================================= */}
                <GovOpportunitiesSection />

                {/* EXPLORE CTA ============================================== */}
                <ExploreCta team={team} />
            </div>

            <CommandBar open={cmdOpen} onClose={() => setCmdOpen(false)} />
            {showFormula && <LaborSavingsModal onClose={() => setShowFormula(false)} />}
        </div>
    );
}

/* ================================================================
   Hero row
   ================================================================ */
function HeroRow({ hero, planChip, isOwner, isLifetime, onRefresh, onNew }) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="atlas-card p-8 relative overflow-hidden"
            style={{
                background: "linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(6,182,212,0.05) 60%, rgba(20,20,42,0.7) 100%)",
                border: "1px solid rgba(139,92,246,0.28)",
            }}
        >
            {/* animated mesh background */}
            <div className="atlas-mesh" aria-hidden>
                <div className="atlas-mesh__blob atlas-mesh__blob--a" />
                <div className="atlas-mesh__blob atlas-mesh__blob--b" />
                <div className="atlas-mesh__blob atlas-mesh__blob--c" />
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-8 items-end">
                {/* LEFT — headline + meta + actions */}
                <div className="min-w-0">
                    <span className="atlas-hero-eyebrow" data-testid="hero-eyebrow">
                        <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                        Enterprise Decision Support Platform
                    </span>
                    <div
                        className="mt-3 text-[11.5px] font-semibold tracking-widest uppercase"
                        style={{ color: "var(--atlas-text-muted)" }}
                    >
                        {hero ? fmtDateTime(hero.server_time, hero.timezone) : "Loading…"}
                    </div>
                    <h1
                        className="atlas-text-gradient mt-2 text-[44px] sm:text-[56px] lg:text-[60px] font-bold leading-[1.03]"
                        data-testid="hero-welcome"
                    >
                        {hero ? <>Welcome back, {hero.first_name}.</> : <span className="opacity-40">Welcome back…</span>}
                    </h1>
                    <h2
                        className="mt-3 text-lg sm:text-xl font-medium"
                        style={{ color: "var(--atlas-text-secondary)" }}
                        data-testid="hero-subtitle"
                    >
                        Your enterprise decision surface is online.
                    </h2>
                    {hero && (
                        <div
                            className="mt-4 flex items-center gap-2 text-[13px]"
                            style={{ color: "var(--atlas-text-tertiary)" }}
                            data-testid="hero-meta"
                        >
                            <Building2 className="w-3.5 h-3.5" strokeWidth={1.75} style={{ color: "var(--atlas-text-muted)" }} />
                            <span>{hero.company_name}</span>
                            <span className="opacity-60">·</span>
                            <span
                                data-testid={`plan-chip-${hero.plan}`}
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${planChip.cls}`}
                            >
                                {planChip.label}
                            </span>
                        </div>
                    )}
                    <div className="mt-6 flex items-center gap-2 flex-wrap">
                        {isOwner && (
                            <span
                                data-testid="owner-ribbon"
                                className="inline-flex items-center gap-1 rounded-full border border-[color:rgba(245,158,11,0.4)] bg-[color:var(--atlas-warning-muted)] px-2.5 h-7 text-[10.5px] font-bold text-[#fbbf24]"
                            >
                                <Crown className="w-3 h-3" strokeWidth={2.5} /> OWNER
                            </span>
                        )}
                        {isLifetime && !isOwner && (
                            <span
                                data-testid="lifetime-ribbon"
                                className="inline-flex items-center gap-1 rounded-full border border-[color:rgba(16,185,129,0.4)] bg-[color:var(--atlas-success-muted)] px-2.5 h-7 text-[10.5px] font-bold text-[#34d399]"
                            >
                                <Sparkles className="w-3 h-3" strokeWidth={2.5} /> LIFETIME
                            </span>
                        )}
                        <button
                            onClick={onRefresh}
                            title="Refresh dashboard"
                            data-testid="dashboard-refresh"
                            className="w-9 h-9 rounded-full atlas-btn-ghost grid place-items-center"
                        >
                            <RefreshCw className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                        <button
                            onClick={onNew}
                            data-testid="dashboard-new-btn"
                            className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-10 text-[13.5px]"
                        >
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> New
                            <span className="ml-1 text-[9.5px] font-mono bg-white/15 rounded px-1.5 py-0.5">⌘K</span>
                        </button>
                    </div>
                </div>

                {/* RIGHT — Atlas orchestrator SVG */}
                <div className="hidden md:block flex-shrink-0" aria-hidden>
                    <OrchestratorSvg />
                </div>
            </div>
        </motion.section>
    );
}

/* ================================================================
   OrchestratorSvg — Sprint Ib · B1
   Central Atlas orb + 6 department nodes radiating. Violet→cyan
   gradient strokes. Center-only pulse.
   ================================================================ */
const ORCH_DEPTS = [
    { key: "legal",      label: "Legal",      color: "#8b5cf6" },
    { key: "analytics",  label: "Data",       color: "#06b6d4" },
    { key: "executive",  label: "Executive",  color: "#a78bfa" },
    { key: "hr",         label: "People",     color: "#10b981" },
    { key: "gtm",        label: "GTM",        color: "#f59e0b" },
    { key: "engineering",label: "Engineering",color: "#ec4899" },
];

function OrchestratorSvg() {
    const CX = 240, CY = 170;
    const R  = 118;
    // 6 evenly-spaced nodes on a ring
    const nodes = ORCH_DEPTS.map((d, i) => {
        const angle = (Math.PI * 2 * i) / ORCH_DEPTS.length - Math.PI / 2;
        return {
            ...d,
            x: CX + R * Math.cos(angle),
            y: CY + R * Math.sin(angle),
        };
    });
    return (
        <svg
            data-testid="apphome-hero-svg"
            width="480" height="340" viewBox="0 0 480 340"
            xmlns="http://www.w3.org/2000/svg"
            style={{ maxWidth: "100%", height: "auto" }}
        >
            <defs>
                <linearGradient id="orch-line" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%"  stopColor="#8b5cf6" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.6" />
                </linearGradient>
                <radialGradient id="orch-orb" cx="50%" cy="45%" r="60%">
                    <stop offset="0%"  stopColor="#e0d4ff" stopOpacity="1" />
                    <stop offset="35%" stopColor="#a78bfa" stopOpacity="1" />
                    <stop offset="70%" stopColor="#7c3aed" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#4338ca" stopOpacity="0.8" />
                </radialGradient>
                <radialGradient id="orch-halo" cx="50%" cy="50%" r="55%">
                    <stop offset="0%"  stopColor="#a78bfa" stopOpacity="0.55" />
                    <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                </radialGradient>
                <filter id="orch-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3.5" result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                {/* Per-node glow filter shared */}
                <filter id="orch-node-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2.2" result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <style>{`
                    @keyframes orb-halo-pulse {
                        0%,100% { transform: scale(1);   opacity: 0.85; }
                        50%     { transform: scale(1.08); opacity: 1; }
                    }
                    @keyframes orb-ring-spin { to { transform: rotate(360deg); } }
                    .orb-halo     { transform-origin: 240px 170px; animation: orb-halo-pulse 3.4s ease-in-out infinite; }
                    .orb-ring     { transform-origin: 240px 170px; animation: orb-ring-spin  22s linear infinite; }
                    .orb-label    { font: 700 11px/1 "Inter", -apple-system, "SF Pro Display", sans-serif; letter-spacing: 0.16em; }
                    .node-label   { font: 600 10px/1 "Inter", -apple-system, "SF Pro Display", sans-serif; letter-spacing: 0.12em; text-transform: uppercase; }
                `}</style>
            </defs>

            {/* orbital ring (dashed, slowly rotating for subtle life) */}
            <g className="orb-ring">
                <circle
                    cx={CX} cy={CY} r={R}
                    fill="none"
                    stroke="url(#orch-line)"
                    strokeWidth="1"
                    strokeDasharray="2 6"
                    strokeOpacity="0.55"
                />
            </g>

            {/* connection strokes (behind nodes) */}
            <g stroke="url(#orch-line)" strokeWidth="1.2" strokeOpacity="0.55" fill="none">
                {nodes.map((n, i) => (
                    <line key={i} x1={CX} y1={CY} x2={n.x} y2={n.y} />
                ))}
            </g>

            {/* department nodes */}
            <g filter="url(#orch-node-glow)">
                {nodes.map((n) => (
                    <g key={n.key}>
                        <circle
                            cx={n.x} cy={n.y} r="12"
                            fill={n.color} fillOpacity="0.22"
                            stroke={n.color} strokeWidth="1.4"
                        />
                        <circle cx={n.x} cy={n.y} r="4" fill={n.color} />
                    </g>
                ))}
            </g>

            {/* department labels */}
            {nodes.map((n) => {
                // push label outward on the axis from center
                const dx = n.x - CX, dy = n.y - CY;
                const len = Math.sqrt(dx * dx + dy * dy) || 1;
                const lx = n.x + (dx / len) * 22;
                const ly = n.y + (dy / len) * 22 + 4;
                const anchor = dx > 8 ? "start" : dx < -8 ? "end" : "middle";
                return (
                    <text
                        key={n.key}
                        x={lx} y={ly}
                        textAnchor={anchor}
                        className="node-label"
                        fill="var(--atlas-text-tertiary)"
                    >
                        {n.label}
                    </text>
                );
            })}

            {/* center halo (pulsing) */}
            <g className="orb-halo">
                <circle cx={CX} cy={CY} r="58" fill="url(#orch-halo)" />
            </g>

            {/* center orb */}
            <g filter="url(#orch-glow)">
                <circle cx={CX} cy={CY} r="34" fill="url(#orch-orb)" />
                {/* highlight */}
                <ellipse cx={CX - 10} cy={CY - 12} rx="10" ry="6" fill="#ffffff" fillOpacity="0.28" />
            </g>

            {/* ATLAS wordmark inside orb */}
            <text
                x={CX} y={CY + 3}
                textAnchor="middle"
                className="orb-label"
                fill="#ffffff"
                style={{ textShadow: "0 1px 6px rgba(0,0,0,0.55)" }}
            >
                ATLAS
            </text>
            <text
                x={CX} y={CY + 16}
                textAnchor="middle"
                fill="rgba(255,255,255,0.72)"
                style={{ font: "600 8px/1 'Inter', -apple-system, sans-serif", letterSpacing: "0.14em" }}
            >
                META-ORCHESTRATOR
            </text>

            {/* base caption */}
            <text
                x={CX} y={310}
                textAnchor="middle"
                fill="var(--atlas-text-muted)"
                style={{ font: "600 9px/1 'Inter', sans-serif", letterSpacing: "0.18em", textTransform: "uppercase" }}
            >
                Your Digital Workforce · One Orchestrator
            </text>
        </svg>
    );
}

/* ================================================================
   KPI card
   ================================================================ */
function KpiCard({ tile, onOpen, starMetric = false }) {
    const Icon = KPI_ICONS[tile.icon] || Info;
    const clickable = !!tile.deep_link || !!tile.modal;
    const animated = useCountUp(tile.value || 0, 900);
    const shown = Math.round(animated);
    const displayValue = tile.format === "usd_month" ? fmtUsd(shown) : fmtNumber(shown);

    return (
        <button
            disabled={!clickable}
            onClick={onOpen}
            data-testid={`kpi-tile-${tile.key}`}
            className={`atlas-card p-5 flex flex-col items-start text-left h-[180px] w-full transition-all ${
                clickable ? "cursor-pointer" : "cursor-default"
            } ${clickable ? "atlas-card--hoverable" : ""}`}
        >
            <div className="flex items-center justify-between w-full">
                <div
                    className="w-10 h-10 rounded-xl grid place-items-center relative"
                    style={{
                        background: "linear-gradient(135deg, rgba(139,92,246,0.22), rgba(139,92,246,0.06))",
                        border: "1px solid rgba(139,92,246,0.35)",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                    }}
                >
                    <Icon className="w-4 h-4" strokeWidth={1.75} style={{ color: "var(--atlas-primary-hover)" }} />
                </div>
                {typeof tile.trend_pct === "number" && (
                    <span
                        data-testid={`kpi-trend-${tile.key}`}
                        className={`text-[10.5px] font-semibold rounded-full px-2 py-0.5 border ${
                            tile.trend_pct >= 0
                                ? "bg-[color:var(--atlas-success-muted)] text-[#34d399] border-[color:rgba(16,185,129,0.45)]"
                                : "bg-[color:var(--atlas-danger-muted)] text-[#fca5a5] border-[color:rgba(239,68,68,0.45)]"
                        }`}
                    >
                        {tile.trend_pct >= 0 ? "▲" : "▼"} {Math.abs(tile.trend_pct)}%
                    </span>
                )}
            </div>
            <div
                className="mt-4 text-[44px] sm:text-[52px] font-bold leading-none tabular-nums"
                style={{
                    color: "var(--atlas-text-primary)",
                    letterSpacing: "-0.03em",
                    textShadow: starMetric ? "0 0 24px rgba(139, 92, 246, 0.35)" : "none",
                }}
                data-testid={`kpi-value-${tile.key}`}
            >
                {displayValue}
                {tile.format === "usd_month" && (
                    <span className="text-base font-normal ml-1" style={{ color: "var(--atlas-text-muted)" }}>/mo</span>
                )}
            </div>
            <div
                className="mt-auto pt-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] leading-tight"
                style={{ color: "var(--atlas-text-muted)" }}
            >
                {tile.label}
            </div>
        </button>
    );
}

/* ================================================================
   Quick actions row
   ================================================================ */
function QuickActionsRow({ onNew }) {
    const items = [
        { to: "/app/workforce",         label: "Activate Workforce Member", Icon: Users,      testid: "quick-hire" },
        { to: "/app/legal/matters",     label: "Open a Matter",       Icon: Landmark,   testid: "quick-matter" },
        { to: "/app/documents",         label: "Draft Document",      Icon: FileText,   testid: "quick-doc" },
        { to: "/app/meetings",          label: "New Meeting",         Icon: Calendar,   testid: "quick-meeting" },
        { to: "/app/knowledge",         label: "Knowledge Base",      Icon: Briefcase,  testid: "quick-knowledge" },
        { to: "/app/workforce/hired",   label: "Manage Workforce",    Icon: Activity,   testid: "quick-manage" },
    ];
    return (
        <motion.section
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15 }}
            className="atlas-card flex items-center justify-between gap-2 flex-wrap p-3"
            data-testid="quick-actions"
        >
            <div className="flex items-center gap-2 pl-1 pr-2">
                <div
                    className="w-8 h-8 rounded-lg grid place-items-center"
                    style={{
                        background: "linear-gradient(135deg, rgba(139,92,246,0.24), rgba(139,92,246,0.08))",
                        border: "1px solid rgba(139,92,246,0.4)",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)",
                    }}
                >
                    <Zap className="w-3.5 h-3.5" style={{ color: "var(--atlas-primary-hover)" }} strokeWidth={2} />
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--atlas-text-muted)" }}>
                    Quick Actions
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {items.map((it) => (
                    <Link
                        key={it.to}
                        to={it.to}
                        data-testid={it.testid}
                        className="inline-flex items-center gap-1.5 rounded-lg atlas-btn-ghost px-3 h-9 text-[12.5px] font-medium"
                    >
                        <it.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {it.label}
                    </Link>
                ))}
                <button
                    onClick={onNew}
                    data-testid="quick-command-bar"
                    className="inline-flex items-center gap-1.5 rounded-lg atlas-btn-primary px-3 h-9 text-[12.5px]"
                >
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Command
                    <span className="ml-1 text-[9.5px] font-mono bg-white/15 rounded px-1.5 py-0.5">⌘K</span>
                </button>
            </div>
        </motion.section>
    );
}

/* ================================================================
   Section header — small violet dot + eyebrow
   ================================================================ */
function SectionHeader({ eyebrow, title, right, eyebrowColor }) {
    return (
        <div className="flex items-start justify-between mb-3">
            <div>
                <div className="atlas-label" style={eyebrowColor ? { color: eyebrowColor } : undefined}>
                    <span className="atlas-section-dot" /> {eyebrow}
                </div>
                <h3 className="mt-1 text-[18px] font-semibold" style={{ color: "var(--atlas-text-primary)", letterSpacing: "-0.015em" }}>
                    {title}
                </h3>
            </div>
            {right}
        </div>
    );
}

/* ================================================================
   Workforce mix (donut)
   ================================================================ */
function WorkforceMixCard({ data, total }) {
    const hasData = data && data.length > 0;
    return (
        <section className="atlas-card p-5 h-full" data-testid="workforce-mix">
            <SectionHeader
                eyebrow="Your Digital Workforce"
                title="Workforce Members by department"
                right={<span className="text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>{total} active</span>}
            />
            <div className="h-[240px] flex items-center gap-4">
                {hasData ? (
                    <>
                        <div className="relative w-[55%] h-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <defs>
                                        {CHART_COLORS.map((c, i) => (
                                            <radialGradient key={i} id={`donutGrad${i}`} cx="30%" cy="30%" r="80%">
                                                <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                                                <stop offset="100%" stopColor={c} stopOpacity={0.65} />
                                            </radialGradient>
                                        ))}
                                    </defs>
                                    <Pie
                                        data={data}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={62}
                                        outerRadius={96}
                                        paddingAngle={2}
                                        stroke="var(--atlas-bg)"
                                        strokeWidth={2}
                                        animationDuration={800}
                                        animationEasing="ease-out"
                                    >
                                        {data.map((_, i) => <Cell key={i} fill={`url(#donutGrad${i % CHART_COLORS.length})`} />)}
                                    </Pie>
                                    <RTooltip
                                        contentStyle={{
                                            background: "var(--atlas-bg-elevated)",
                                            border: "1px solid var(--atlas-primary)",
                                            borderRadius: 8,
                                            color: "var(--atlas-text-primary)",
                                            fontSize: 12,
                                            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                                        }}
                                        itemStyle={{ color: "var(--atlas-text-primary)" }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Donut center label */}
                            <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                <div className="text-center">
                                    <div className="text-[26px] font-bold tabular-nums" style={{ color: "var(--atlas-text-primary)", letterSpacing: "-0.02em" }}>
                                        {total}
                                    </div>
                                    <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--atlas-text-muted)" }}>
                                        Active
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ul className="flex-1 space-y-2 max-h-full overflow-y-auto atlas-scrollbar pr-1" data-testid="workforce-mix-legend">
                            {data.map((d, i) => (
                                <li key={d.name} className="flex items-center gap-2 text-[13px]" style={{ color: "var(--atlas-text-secondary)" }}>
                                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length], boxShadow: `0 0 8px ${CHART_COLORS[i % CHART_COLORS.length]}88` }} />
                                    <span className="flex-1 truncate">{d.name}</span>
                                    <span className="tabular-nums font-semibold" style={{ color: "var(--atlas-text-primary)" }}>{d.value}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                ) : (
                    <EmptyBlock
                        icon={Users}
                        text="No active employees yet."
                        cta={{ to: "/app/workforce", label: "Browse workforce" }}
                    />
                )}
            </div>
        </section>
    );
}

/* ================================================================
   Performance line chart
   ================================================================ */
function PerformanceCard({ data, tasksTotal }) {
    return (
        <section className="atlas-card p-5 h-full" data-testid="performance-card">
            <SectionHeader
                eyebrow="Performance Signals"
                title="Tasks completed · last 7 days"
                right={<span className="text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>{tasksTotal} total</span>}
            />
            <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 12, right: 8, left: -12, bottom: 0 }}>
                        <defs>
                            <linearGradient id="atlasPerfFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%"   stopColor="#8b5cf6" stopOpacity={0.55} />
                                <stop offset="55%"  stopColor="#06b6d4" stopOpacity={0.25} />
                                <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="atlasPerfStroke" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%"   stopColor="#a78bfa" />
                                <stop offset="100%" stopColor="#06b6d4" />
                            </linearGradient>
                            <filter id="perfDotGlow" x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                <feMerge>
                                    <feMergeNode in="coloredBlur" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </defs>
                        <CartesianGrid strokeDasharray="3 4" stroke="var(--atlas-border-strong)" strokeOpacity={0.4} vertical={false} />
                        <XAxis dataKey="label" stroke="var(--atlas-text-muted)" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} stroke="var(--atlas-text-muted)" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={28} />
                        <RTooltip
                            cursor={{ stroke: "var(--atlas-primary)", strokeWidth: 1, strokeDasharray: "3 3" }}
                            contentStyle={{
                                background: "var(--atlas-bg-elevated)",
                                border: "1px solid var(--atlas-primary)",
                                borderRadius: 8,
                                color: "var(--atlas-text-primary)",
                                fontSize: 12,
                                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                            }}
                            itemStyle={{ color: "var(--atlas-text-primary)" }}
                            labelStyle={{ color: "var(--atlas-text-tertiary)" }}
                        />
                        <Area
                            type="monotone"
                            dataKey="tasks"
                            stroke="url(#atlasPerfStroke)"
                            strokeWidth={2.5}
                            fill="url(#atlasPerfFill)"
                            dot={{ r: 3.5, stroke: "#a78bfa", strokeWidth: 1.75, fill: "var(--atlas-bg-elevated)", filter: "url(#perfDotGlow)" }}
                            activeDot={{ r: 5.5, stroke: "#a78bfa", strokeWidth: 2, fill: "#0a0a1a", filter: "url(#perfDotGlow)" }}
                            animationDuration={900}
                            animationEasing="ease-out"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </section>
    );
}

/* ================================================================
   Daily briefing card
   ================================================================ */
function BriefingCard({ briefing, onRefresh, busy, disabled, hasNothing }) {
    return (
        <section className="atlas-card p-6 h-full" data-testid="daily-briefing">
            <SectionHeader
                eyebrow="Atlas Briefing"
                title="Your Daily Briefing"
                right={
                    <button
                        onClick={onRefresh}
                        disabled={busy || disabled}
                        title={disabled ? "Read-only in impersonation mode" : "Refresh briefing"}
                        data-testid="briefing-refresh"
                        className="atlas-btn-ghost inline-flex items-center gap-1 rounded-md px-2.5 h-8 text-[11.5px] font-medium disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} strokeWidth={2} />
                        {busy ? "Refreshing…" : "Refresh"}
                    </button>
                }
            />
            <div className="text-[10.5px] mt-[-4px] mb-4" style={{ color: "var(--atlas-text-muted)" }}>
                Powered by Claude Sonnet 4.5
            </div>
            {!briefing && (
                <ul className="space-y-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <li key={i} className="flex gap-2 items-start">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--atlas-border-strong)" }} />
                            <div className="flex-1 h-3 rounded animate-pulse" style={{ width: `${70 + i * 5}%`, background: "var(--atlas-surface-alt)" }} />
                        </li>
                    ))}
                </ul>
            )}
            {briefing && (
                <ul className="space-y-3" data-testid="briefing-bullets">
                    {(briefing.bullets || []).map((b, i) => (
                        <li key={i} className="flex gap-2.5 items-start text-[14px] leading-relaxed" style={{ color: "var(--atlas-text-secondary)" }}>
                            <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--atlas-primary)", boxShadow: "0 0 6px rgba(139,92,246,0.6)" }} />
                            <span dangerouslySetInnerHTML={{ __html: renderInlineBold(b) }} />
                        </li>
                    ))}
                </ul>
            )}
            {briefing?.generated_at && (
                <div className="mt-4 text-[10.5px]" style={{ color: "var(--atlas-text-subtle)" }} data-testid="briefing-ts">
                    Generated {relTime(briefing.generated_at)} · source: {briefing.source}
                </div>
            )}
            {hasNothing && (
                <div className="mt-3">
                    <Link
                        to="/app/workforce"
                        data-testid="briefing-marketplace-cta"
                        className="inline-flex items-center gap-1 text-[12.5px] font-medium"
                        style={{ color: "var(--atlas-primary-hover)" }}
                    >
                        Browse the marketplace <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </Link>
                </div>
            )}
        </section>
    );
}

function renderInlineBold(text) {
    const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return escape(text).replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>");
}

/* ================================================================
   Top Performers
   ================================================================ */
function TopPerformersCard({ performers }) {
    const list = performers || [];
    return (
        <section className="atlas-card p-5 h-full" data-testid="top-performers">
            <SectionHeader
                eyebrow="Top Performing Workforce Members"
                title="This month"
                right={<TrendingUp className="w-4 h-4" style={{ color: "var(--atlas-primary-hover)" }} strokeWidth={2} />}
            />
            {!performers ? (
                <ul className="space-y-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <li key={i} className="h-11 rounded-md animate-pulse" style={{ background: "var(--atlas-surface-alt)" }} />
                    ))}
                </ul>
            ) : list.length === 0 ? (
                <EmptyBlock icon={Sparkles} text="No completed tasks yet this month." cta={{ to: "/app/workforce", label: "Activate your first" }} />
            ) : (
                <ol className="space-y-2" data-testid="top-performers-list">
                    {list.slice(0, 5).map((p, i) => (
                        <li key={p.hired_worker_id}>
                            <Link
                                to={`/app/workers/${p.hired_worker_id}`}
                                data-testid={`top-performer-${p.worker_slug}`}
                                className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors"
                                style={{ background: "transparent" }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(139,92,246,0.08)")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            >
                                <span
                                    className="w-6 h-6 rounded-md grid place-items-center text-[11px] font-bold tabular-nums"
                                    style={{
                                        background: i === 0 ? "linear-gradient(135deg, rgba(139,92,246,0.35), rgba(139,92,246,0.12))" : "var(--atlas-bg-highlight)",
                                        color: i === 0 ? "#fff" : "var(--atlas-text-tertiary)",
                                        border: `1px solid ${i === 0 ? "rgba(139,92,246,0.55)" : "var(--atlas-border-strong)"}`,
                                        boxShadow: i === 0 ? "0 0 12px rgba(139,92,246,0.4)" : "none",
                                    }}
                                >
                                    {i + 1}
                                </span>
                                <img
                                    src={resolveAvatar(p.avatar_url)}
                                    alt={p.name}
                                    className="w-9 h-9 rounded-full object-cover"
                                    style={{ border: "1px solid var(--atlas-border-strong)" }}
                                    loading="lazy"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[13px] font-semibold truncate" style={{ color: "var(--atlas-text-primary)" }}>
                                        {p.name}
                                    </div>
                                    <div className="text-[10.5px] uppercase tracking-[0.12em] truncate" style={{ color: "var(--atlas-text-muted)" }}>
                                        {p.role}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[14px] font-bold tabular-nums" style={{ color: "var(--atlas-text-primary)" }}>
                                        {p.tasks}
                                    </div>
                                    <div className="text-[9.5px] uppercase tracking-[0.12em]" style={{ color: "var(--atlas-text-subtle)" }}>
                                        tasks
                                    </div>
                                </div>
                            </Link>
                        </li>
                    ))}
                </ol>
            )}
        </section>
    );
}

/* ================================================================
   Recent activity
   ================================================================ */
function RecentActivityCard({ activity }) {
    return (
        <section className="atlas-card p-5" data-testid="recent-activity">
            <SectionHeader
                eyebrow="Signal Feed"
                title="Recent Activity"
                right={
                    <Link
                        to="/app/workforce/hired"
                        data-testid="activity-view-all"
                        className="text-[12px] font-medium inline-flex items-center gap-1"
                        style={{ color: "var(--atlas-primary-hover)" }}
                    >
                        View all <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </Link>
                }
            />
            {activity.length === 0 ? (
                <EmptyBlock icon={MessageSquare} text="No activity yet — activate a Workforce Member to see them get to work." cta={{ to: "/app/workforce", label: "Browse marketplace" }} />
            ) : (
                <ul className="divide-y" style={{ borderColor: "var(--atlas-border-strong)" }} data-testid="activity-list">
                    {activity.slice(0, 8).map((a) => (
                        <li key={a.id} data-testid={`activity-row-${a.id}`} className="py-3 flex items-center gap-3">
                            {a._worker ? (
                                <Link to={`/app/workers/${a._worker.hired_worker_id}`}>
                                    <img
                                        src={resolveAvatar(a._worker.avatar_url)}
                                        alt={a._worker.name}
                                        className="w-8 h-8 rounded-full object-cover"
                                        style={{ border: "1px solid var(--atlas-border-strong)" }}
                                    />
                                </Link>
                            ) : (
                                <span
                                    className="w-8 h-8 rounded-full grid place-items-center"
                                    style={{ background: "var(--atlas-bg-highlight)", border: "1px solid var(--atlas-border-strong)" }}
                                >
                                    <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--atlas-text-tertiary)" }} strokeWidth={1.75} />
                                </span>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="text-[13.5px] truncate" style={{ color: "var(--atlas-text-secondary)" }}>
                                    {a._worker && (
                                        <b style={{ color: "var(--atlas-text-primary)" }}>{a._worker.name} </b>
                                    )}
                                    <span>{a.title}</span>
                                </div>
                            </div>
                            <StatusPill status={a.status} />
                            <span className="text-[11px] tabular-nums w-16 text-right" style={{ color: "var(--atlas-text-subtle)" }}>
                                {relTime(a.timestamp)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function StatusPill({ status }) {
    const map = {
        done:    { cls: "atlas-pill--success", label: "DONE" },
        running: { cls: "atlas-pill--info",    label: "RUN" },
        failed:  { cls: "atlas-pill--danger",  label: "FAIL" },
        paused:  { cls: "atlas-pill--warning", label: "PAUSED" },
    };
    const m = map[status] || map.running;
    return (
        <span className={`atlas-pill ${m.cls} text-[9.5px] font-semibold uppercase tracking-[0.14em]`}>
            {m.label}
        </span>
    );
}

/* ================================================================
   Government Opportunities (mocked)
   ================================================================ */
function GovOpportunitiesSection() {
    const today = new Date();
    return (
        <section data-testid="gov-opportunities">
            <div className="flex items-start justify-between mb-3">
                <div>
                    <div className="atlas-label" style={{ color: "var(--atlas-primary-hover)" }}>
                        <span className="atlas-section-dot" />
                        <Landmark className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={2} />
                        Government Opportunities
                    </div>
                    <h3 className="mt-1 text-[18px] font-semibold" style={{ color: "var(--atlas-text-primary)", letterSpacing: "-0.015em" }}>
                        Federal contract signal preview
                    </h3>
                </div>
                <span className="atlas-pill atlas-pill--primary text-[9.5px] uppercase tracking-[0.14em]" data-testid="gov-illustrative-header">
                    Illustrative
                </span>
            </div>

            <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                data-testid="gov-opps-grid"
            >
                {GOV_OPPS.map((o) => {
                    const deadline = new Date(today.getTime() + o.deadline_days * 86400000);
                    const dateStr = deadline.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                    return (
                        <motion.div
                            key={o.title}
                            variants={rise}
                            data-testid={`gov-opp-${o.agency.toLowerCase()}`}
                            className="atlas-card atlas-card--hoverable p-5 relative overflow-hidden"
                        >
                            <span
                                className="absolute top-3 right-3 text-[8.5px] font-bold uppercase tracking-[0.16em] rounded-sm px-1.5 py-0.5"
                                style={{
                                    background: "var(--atlas-primary-muted)",
                                    color: "var(--atlas-primary-hover)",
                                    border: "1px solid rgba(139,92,246,0.4)",
                                }}
                            >
                                Illustrative
                            </span>
                            <div className="flex items-center gap-2">
                                <span
                                    className="inline-flex items-center gap-1 rounded-md px-2 h-6 text-[10.5px] font-bold uppercase tracking-[0.14em]"
                                    style={{
                                        background: "var(--atlas-bg-highlight)",
                                        color: "var(--atlas-text-primary)",
                                        border: "1px solid var(--atlas-border-strong)",
                                    }}
                                >
                                    <Building2 className="w-3 h-3" strokeWidth={2} />
                                    {o.agency}
                                </span>
                                <span className="text-[10px]" style={{ color: "var(--atlas-text-muted)" }}>
                                    NAICS {o.naics}
                                </span>
                            </div>
                            <div className="mt-3 text-[14.5px] font-semibold leading-snug" style={{ color: "var(--atlas-text-primary)" }}>
                                {o.title}
                            </div>
                            <div className="mt-3 flex items-baseline gap-2">
                                <div className="text-[26px] font-bold tabular-nums" style={{ color: "var(--atlas-text-primary)", letterSpacing: "-0.02em" }}>
                                    {o.value_label}
                                </div>
                                <div className="text-[10.5px] uppercase tracking-[0.14em]" style={{ color: "var(--atlas-text-muted)" }}>
                                    ceiling
                                </div>
                            </div>
                            <div className="mt-3">
                                <div className="flex items-center justify-between text-[10.5px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--atlas-text-muted)" }}>
                                    <span>Fit score</span>
                                    <span className="tabular-nums font-semibold" style={{ color: "var(--atlas-primary-hover)" }}>{o.fit_score}%</span>
                                </div>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--atlas-bg-highlight)" }}>
                                    <div
                                        className="h-full rounded-full"
                                        style={{
                                            width: `${o.fit_score}%`,
                                            background: "linear-gradient(90deg, #8b5cf6, #a78bfa 60%, #06b6d4 130%)",
                                            boxShadow: "0 0 10px rgba(139,92,246,0.65)",
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="mt-4 pt-3 flex items-center justify-between text-[11px] border-t" style={{ borderColor: "var(--atlas-border-strong)" }}>
                                <span style={{ color: "var(--atlas-text-tertiary)" }}>Response due</span>
                                <span
                                    className="atlas-pill atlas-pill--warning text-[10px] tabular-nums"
                                    data-testid={`gov-opp-deadline-${o.agency.toLowerCase()}`}
                                >
                                    {dateStr}
                                </span>
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>
            <p
                className="mt-4 text-[12.5px] leading-relaxed"
                style={{ color: "var(--atlas-text-tertiary)" }}
                data-testid="gov-opps-explainer"
            >
                This preview shows what the Government Affairs module will surface once activated.
                Live SAM.gov + agency feed integration coming in Phase 5b.
            </p>
        </section>
    );
}

/* ================================================================
   Explore CTA
   ================================================================ */
function ExploreCta({ team }) {
    const active = team?.active_count ?? 0;
    const cap = team?.unlimited ? "Unlimited" : team?.cap ?? "—";
    return (
        <section
            data-testid="explore-cta"
            className="atlas-card p-6 relative overflow-hidden"
            style={{
                background: "linear-gradient(120deg, rgba(139,92,246,0.14) 0%, rgba(6,182,212,0.06) 100%)",
                border: "1px solid rgba(139,92,246,0.32)",
            }}
        >
            <div
                aria-hidden
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl"
                style={{ background: "rgba(139,92,246,0.35)" }}
            />
            <div
                aria-hidden
                className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full blur-3xl"
                style={{ background: "rgba(6,182,212,0.25)" }}
            />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div className="flex-1 min-w-0">
                    <div className="atlas-label" style={{ color: "var(--atlas-primary-hover)" }}>
                        <span className="atlas-section-dot" /> Marketplace
                    </div>
                    <h3 className="mt-2 text-2xl font-bold tracking-tight" style={{ color: "var(--atlas-text-primary)" }}>
                        Explore the Workforce Marketplace
                    </h3>
                    <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-secondary)" }}>
                        22 Workforce Members available across 12 departments · {active}/{cap} active in your workspace.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Link
                            to="/app/workforce"
                            data-testid="explore-primary"
                            className="atlas-btn-primary inline-flex items-center gap-1 rounded-lg px-4 h-10 text-[13.5px]"
                        >
                            <Store className="w-4 h-4" strokeWidth={2} /> Browse marketplace <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                        </Link>
                        <Link
                            to="/pricing"
                            data-testid="explore-secondary"
                            className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-4 h-10 text-[13.5px] font-medium"
                        >
                            Compare plans
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ================================================================
   Helpers
   ================================================================ */
function EmptyBlock({ icon: Icon, text, cta }) {
    return (
        <div className="w-full text-center py-6">
            <Icon className="w-7 h-7 mx-auto mb-3" style={{ color: "var(--atlas-text-muted)" }} strokeWidth={1.5} />
            <div className="text-[13px]" style={{ color: "var(--atlas-text-secondary)" }}>{text}</div>
            {cta && (
                <Link
                    to={cta.to}
                    className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-medium"
                    style={{ color: "var(--atlas-primary-hover)" }}
                >
                    {cta.label} <ChevronRight className="w-3 h-3" strokeWidth={2} />
                </Link>
            )}
        </div>
    );
}

function SkeletonGrid({ rows, cols, height }) {
    return (
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${cols} gap-4`}>
            {Array.from({ length: rows * cols }).map((_, i) => (
                <div key={i} className={`atlas-card ${height} animate-pulse`} style={{ background: "var(--atlas-surface-alt)" }} />
            ))}
        </div>
    );
}

function LaborSavingsModal({ onClose }) {
    return (
        <div
            className="fixed inset-0 grid place-items-center z-[100] p-4"
            style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="labor-savings-modal"
        >
            <div className="atlas-card w-full max-w-[520px] p-6">
                <div className="text-lg font-bold" style={{ color: "var(--atlas-text-primary)" }}>How this is computed</div>
                <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: "var(--atlas-text-secondary)" }}>
                    We sum, across every <b style={{ color: "var(--atlas-text-primary)" }}>completed</b> activity this month, the value{" "}
                    <code
                        className="rounded px-1.5 py-0.5 text-[12px]"
                        style={{ background: "var(--atlas-bg-highlight)", color: "var(--atlas-secondary)", border: "1px solid var(--atlas-border-strong)" }}
                    >
                        hourly_equivalent × avg_hours_saved_per_task
                    </code>{" "}
                    for the Workforce Member that produced it. Every catalog Workforce Member has a benchmarked hourly equivalent (mid-market senior in their role) and a benchmarked hours-per-task savings.
                </p>
                <p className="mt-3 text-[12px] leading-relaxed" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Numbers are your tenant's data only, computed server-side. Never fabricated.
                </p>
                <div className="mt-5 text-right">
                    <button
                        onClick={onClose}
                        className="atlas-btn-ghost rounded-lg px-4 h-9 text-sm"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
